import { Component, DestroyRef, inject, input, OnDestroy, OnInit } from '@angular/core';
import {DetailsConfig, SharedUiDetailsModule} from '@snelstart/ui-details';
import {
  AddressModel,
  BokkzAlgDataService,
  BokkzAlgLogic,
  BokkzClient, ClientComponent,
  ClientEntity,
  CustomerClient,
  CustomerQueryClient,
  CustomerRequest,
  CustomerResponse,
  FiscalIdentifierType,
  IntermediaryEntity,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {environment} from '../../../../../../../apps/shells/suite-shell/src/environments/environment';
import {FormControl, FormGroup} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {filter, forkJoin, map, shareReplay, switchMap, tap} from 'rxjs';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiPanelModule} from '@snelstart/ui-panel';
import {SharedUiBadgeModule} from '@snelstart/ui-badge';
import {CommonModule} from '@angular/common';
import {SharedUiCardModule} from '@snelstart/ui-card';
import {ToastService} from '@snelstart/ui-toast';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {dispatchSavedItemEvent} from '@snelstart/domain-logic';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {SharedUiLoadingModule} from "@snelstart/ui-loading";

@Component({
    selector: 'snelstart-venture-fiscaal-feature-tax-client-details',
    templateUrl: './feature-tax-client-details.component.html',
    styleUrls: ['./feature-tax-client-details.component.scss'],
    imports: [
        SharedUiDetailsModule,
        SharedUiButtonModule,
        SharedUiPanelModule,
        SharedUiBadgeModule,
        CommonModule,
        SharedUiCardModule,
        SharedUiAlertModule,
        ClientComponent,
        SharedUiLoadingModule,
    ],
})
export class FeatureTaxClientDetailsComponent implements OnInit, OnDestroy {
    public id = input.required<string>();

    private readonly destroyRef = inject(DestroyRef);
    private readonly bokkzClient = inject(BokkzClient);
    private readonly algDataService = inject(BokkzAlgDataService);
    private readonly toastService = inject(ToastService);
    private readonly algLogic = inject(BokkzAlgLogic);
    private readonly customerClient = inject(CustomerClient);
    private readonly customerQueryClient = inject(CustomerQueryClient);
    private readonly activatedRoute = inject(ActivatedRoute);

    public dialogClientSettingsVisible = false;

    public detailsConfig = DetailsConfig.create({
        hideArrowButtons: false,
    });

    public clientId: any;
    public sstPrimaryAdminId: any;
    public customerResponse = {} as CustomerResponse;
    public clientEntity = {} as ClientEntity;
    public isLoading = true;
    public intermediaryEntity = {} as IntermediaryEntity;

    protected readonly intermediary$ = this.algDataService.intermediaryEntity$.pipe(
        filter((intermediary) => !!intermediary?.sstPrimaryAdminId),
        tap((intermediary) => {
            this.sstPrimaryAdminId = intermediary.sstPrimaryAdminId;
            this.intermediaryEntity = intermediary;
        }),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly client$ = this.algDataService.clientEntity$.pipe(
        filter((client) => !!client?.sstRelationId),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public formGroup = new FormGroup({
        id: new FormControl<number | null>(null),
        sstRelationId: new FormControl<string | null>(null),
        activeMachtiging: new FormControl<boolean | null>(false),
        deleted: new FormControl<boolean | null>(false),
        taxId: new FormControl<string | null>(null),
        taxIdVat: new FormControl<string | null>(null),
        taxIdWageTax: new FormControl<string | null>(null),
        // adminEntities: new FormControl<AdminEntity[] | null>(null),
        machtigingStatusToelichting: new FormControl<string | null>(null),
        typeClient: new FormControl<string | null>(null),
        rechtsvorm: new FormControl<string | null>(null),
        aanspreekTitel: new FormControl<string | null>(null),
        voorletters: new FormControl<string | null>(null),
        tussenvoegsel: new FormControl<string | null>(null),
        achternaam: new FormControl<string | null>(null),
        roepnaam: new FormControl<string | null>(null),
        name: new FormControl<string | null>(null),
        postponement: new FormControl<boolean | null>(false),
        incomeTax: new FormControl<boolean | null>(false),
        valueAddedTax: new FormControl<boolean | null>(false),
        corporateTax: new FormControl<boolean | null>(false),
        wageTax: new FormControl<boolean | null>(false),
        kvkId: new FormControl<string | null>(null),
        classificatie: new FormControl<string | null>(null),
        sbiCode: new FormControl<string | null>(null),
        userIdGemachtigde: new FormControl<number | null>(null),
        deadline: new FormControl<Date | null>(null),
        street: new FormControl<string | null>(null),
        houseNumber: new FormControl<number | null>(null),
        numberAddition: new FormControl<string | null>(null),
        postalCode: new FormControl<string | null>(null),
        place: new FormControl<string | null>(null),
        phone: new FormControl<string | null>(null),
        mobile: new FormControl<string | null>(null),
        email: new FormControl<string | null>(null),
        website: new FormControl<string | null>(null),
    });

    ngOnInit(): void {
        this.client$.pipe(
            tap((client) => {
                console.debug(`FeatureTaxClientDetailsComponent.Client: ${client.name}`, client);

                this.clientEntity = client;
                this.formGroup.reset();
                this.formGroup.patchValue(client, { emitEvent: false });
            }),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe();

        this.activatedRoute.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            this.clientId = params['id'];
            if (this.clientId !== this.id()) {
                this.makeCalls();
            }
        });
    }

    ngOnDestroy(): void {
        this.formGroup.reset();
    }

    private makeCalls() {
        this.formGroup.reset();
        this.algDataService.intermediary
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((intermediary) => !!intermediary?.sstPrimaryAdminId),
                switchMap((intermediary) =>
                    this.algLogic.verrijkToken(intermediary.sstPrimaryAdminId!).pipe(
                        switchMap(() =>
                            forkJoin([this.customerQueryClient.getCustomer(this.clientId), this.bokkzClient.getClient(this.clientId)]),
                        ),
                        map(([customer, clientEntity]) => ({ customer, clientEntity })),
                    ),
                ),
            )
            .subscribe({
                next: ({ customer, clientEntity }) => {
                    if (customer && clientEntity == null) {
                        const nieuw = {
                            email: customer.emailAddressLine?.at(0)!,
                            name: customer.name,
                            kvkId: customer.kvk,
                            phone: customer.phoneNumber,
                            mobile: customer.mobileNumber,
                            website: customer.website,
                            street: customer.address?.streetName,
                            houseNumber: customer.address?.streetNumber,
                            numberAddition: customer.address?.affix,
                            postalCode: customer.address?.postalCode,
                            place: customer.address?.city,
                            taxId: customer.fiscalIdentifier,
                            sstRelationId: customer.id,
                            sstPrimaryAdminId: this.sstPrimaryAdminId,
                            sstCode: customer.code,
                        };
                        this.bokkzClient
                            .postClient(nieuw)
                            .pipe(takeUntilDestroyed(this.destroyRef))
                            .subscribe((clientEntityResponse) => {
                                this.algDataService.updateClient(clientEntityResponse);
                            });
                    }
                    if (customer && clientEntity) {
                        this.clientEntity = clientEntity;
                        this.customerResponse = customer;

                        const merged = {
                            ...clientEntity,
                            email: customer.emailAddressLine?.at(0)! || clientEntity.email,
                            name: customer.name,
                            kvkId: customer.kvk || clientEntity.kvkId,
                            phone: customer.phoneNumber || clientEntity.mobile,
                            mobile: customer.mobileNumber || clientEntity.mobile,
                            website: customer.website,
                            street: customer.address?.streetName || clientEntity.street,
                            houseNumber: customer.address?.streetNumber || clientEntity.houseNumber,
                            numberAddition: customer.address?.affix || clientEntity.numberAddition,
                            postalCode: customer.address?.postalCode || clientEntity.postalCode,
                            place: customer.address?.city || clientEntity.place,
                            taxId: customer.fiscalIdentifier ? customer.fiscalIdentifier : clientEntity.taxId,
                            // sbiCode: customer.sb
                        };

                        const updatedCustomerRequest = this.buildUpdatedCustomerRequest(customer, clientEntity);

                        this.bokkzClient
                            .postClient(merged)
                            .pipe(takeUntilDestroyed(this.destroyRef))
                            .subscribe((clientEntityResponse) => {
                                this.algDataService.updateClient(clientEntityResponse);
                            });
                        if (updatedCustomerRequest) {
                            this.customerClient
                                .updateCustomer(updatedCustomerRequest)
                                .pipe(takeUntilDestroyed(this.destroyRef))
                                .subscribe((result) => {
                                    if (result?.id) {
                                        dispatchSavedItemEvent({
                                            identifier: result.id,
                                            groupIdentifier: 'relations',
                                            result: {
                                                id: result.id,
                                                code: result.code,
                                                name: result.name,
                                                contactPersonName: result.contactName,
                                                phoneNumber: result.phoneNumber,
                                                mobilePhoneNumber: result.mobileNumber,
                                                addressPostalCode: result.address?.postalCode,
                                                addressCity: result.address?.city,
                                                email: result.emailAddressLine?.length ? result.emailAddressLine[0] : undefined,
                                            },
                                        });
                                    }
                                });
                        }
                    }
                },
                error: (err) => console.error('Error in makeCalls:', err),
                complete: () => {
                    this.isLoading = false;
                    console.log('complete');
                },
            });
    }

    public showDialogClientSettings() {
        this.dialogClientSettingsVisible = true;
    }

    public openTaxAppInNewTab(taxUrl: string) {
        let url = `${environment.fiscaalBaseUrl}${taxUrl}/${this.clientId}`;
        if (taxUrl.toLowerCase().trim().includes('admin')) {
            const administrationId = this.clientEntity?.adminEntities?.at(0)?.administrationId;
            if (administrationId) {
                let url = `${environment.bookKeepingBaseUrl}${administrationId}`;
                window.open(url, '_blank');
            } else {
                this.toastService.showWarningMessage('Geen administratie.');
            }
        } else {
            window.open(url, '_blank');
        }
    }

    private buildUpdatedCustomerRequest(customer: CustomerResponse, clientEntity: ClientEntity): CustomerRequest | undefined {
        let updateAddress = false;
        let updatePhone = false;
        let updateMobile = false;
        let updateEmail = false;
        let updateKvk = false;
        let updateFiscaalNummer = false;

        const addressMissing =
            !customer.address?.streetName || !customer.address?.streetNumber || !customer.address?.postalCode || !customer.address?.city;

        if (addressMissing) {
            const street = this.normalizeTextOnly(clientEntity.street);
            const houseNumber = this.normalizeHouseNumber(clientEntity.houseNumber);
            const postalCode = this.normalizePostalCode(clientEntity.postalCode);
            const city = this.normalizeTextOnly(clientEntity.place);

            if (street && houseNumber !== undefined && postalCode && city) {
                updateAddress = true;
            }
        }
        if (!customer.fiscalIdentifier && clientEntity.taxId) updateFiscaalNummer = true;
        if (!customer.phoneNumber && clientEntity.phone) updatePhone = true;
        if (!customer.mobileNumber && clientEntity.mobile) updateMobile = true;

        if ((!customer.emailAddressLine || customer.emailAddressLine.length === 0) && clientEntity.email) {
            updateEmail = true;
        }

        if (!customer.kvk && clientEntity.kvkId) updateKvk = true;

        if (!updateAddress && !updatePhone && !updateMobile && !updateEmail && !updateKvk && !updateFiscaalNummer) return undefined;

        const requestData = new CustomerRequest({
            ...customer,
        });
        // This property (customerGroup) is specifically removed as it's deprecated and will result in a 400 Bad Request if included
        delete requestData.customerGroup;

        if (updateAddress) {
            requestData.address = new AddressModel({
                streetName: this.normalizeTextOnly(clientEntity.street),
                streetNumber: this.normalizeHouseNumber(clientEntity.houseNumber),
                affix: clientEntity.numberAddition,
                postalCode: this.normalizePostalCode(clientEntity.postalCode),
                city: this.normalizeTextOnly(clientEntity.place),
            });
        }

        if (updatePhone) {
            requestData.phoneNumber = clientEntity.phone;
        }

        if (updateMobile) {
            requestData.mobileNumber = clientEntity.mobile;
        }

        if (updateEmail && clientEntity.email) {
            requestData.emailAddressLine = [clientEntity.email];
        }

        if (updateKvk) {
            requestData.kvk = clientEntity.kvkId;
        }

        if (updateFiscaalNummer) {
            requestData.fiscalIdentifier = clientEntity.taxId;
            requestData.fiscalIdentifierType =
                clientEntity.incomeTax || clientEntity.typeClient?.includes('natuurlijk')
                    ? FiscalIdentifierType.PersonalIdentity
                    : FiscalIdentifierType.LegalEntityAndPartnershipIdentification;
        }

        return requestData;
    }

    normalizePostalCode(pc: string | undefined | null): string | undefined {
        if (!pc) return undefined;

        // Remove spaces and uppercase
        const clean = pc.replace(/\s+/g, '').toUpperCase();

        // Must match 4 digits + 2 letters
        const match = clean.match(/^(\d{4})([A-Z]{2})$/);
        if (!match) return undefined;

        return `${match[1]} ${match[2]}`;
    }

    normalizeHouseNumber(num: string | number | undefined | null): number | undefined {
        if (num == null) return undefined;

        const digits = String(num).replace(/\D+/g, '');

        if (!digits) return undefined;

        const asNumber = Number(digits);

        return Number.isNaN(asNumber) ? undefined : asNumber;
    }

    normalizeTextOnly(text: string | undefined | null): string | undefined {
        if (!text) return undefined;

        // Keep letters and spaces
        const clean = text.replace(/[^a-zA-ZÀ-ÖØ-öø-ÿ\s]/g, '').trim();
        return clean || undefined;
    }
}
